import { HttpService } from "@root/services/http";
import {
  prepareErrorResponse,
  prepareResponseObject,
} from "@root/services/http/response";
import { RESPONSE_TYPES } from "@root/constants/response-types";
import {
  BRANDING_CONFIG_ENDPOINT,
  BRANDING_BY_DOMAIN_ENDPOINT,
  BRANDING_BY_ID_ENDPOINT,
  BRANDING_LOGO_ENDPOINT,
  BRANDING_COLORS_ENDPOINT,
  BRANDING_TYPOGRAPHY_ENDPOINT,
  BRANDING_LOGIN_PAGE_ENDPOINT,
  BRANDING_LOGIN_BACKGROUND_ENDPOINT,
  BRANDING_EMAIL_ENDPOINT,
  BRANDING_METADATA_ENDPOINT,
  BRANDING_DOMAIN_ENDPOINT,
  BRANDING_DOMAIN_VERIFY_ENDPOINT,
  BRANDING_DOMAIN_STATUS_ENDPOINT,
} from "./endPoints";

export class BrandingService extends HttpService {
  /**
   * Public: Fetch organization branding by domain (no auth required for login page)
   */
  fetchBrandingByDomain = async (
    baseAuthUrl: string,
    domain: string
  ): Promise<any> => {
    try {
      // Backend:
      //  - GET /api/org/public?domain={domain}
      // Extract subdomain from full domain (e.g., "subdomain" from "subdomain.doaz.ai")
      const subdomain = domain
      const url = `${baseAuthUrl}/${BRANDING_BY_DOMAIN_ENDPOINT}`;
      const apiResponse: any = await this.get(url, { domain: subdomain });
      return prepareResponseObject(apiResponse, RESPONSE_TYPES.SUCCESS);
    } catch (error) {
      return prepareErrorResponse(error);
    }
  };

  /**
   * Public: Fetch full organization + branding by ID
   */
  fetchBrandingById = async (
    baseAuthUrl: string,
    id: number | string
  ): Promise<any> => {
    try {
      // Backend:
      //  - GET /api/org/public/{id}
      const url = `${baseAuthUrl}/${BRANDING_BY_ID_ENDPOINT}/${id}`;
      const apiResponse: any = await this.get(url);
      return prepareResponseObject(apiResponse, RESPONSE_TYPES.SUCCESS);
    } catch (error) {
      return prepareErrorResponse(error);
    }
  };

  /**
   * Fetch full branding config for an organization
   */
  fetchBrandingConfig = async (
    baseAuthUrl: string,
    organizationId: string
  ): Promise<any> => {
    try {
      const url = `${baseAuthUrl}/${BRANDING_CONFIG_ENDPOINT}/${organizationId}`;
      const apiResponse: any = await this.get(url);
      return prepareResponseObject(apiResponse, RESPONSE_TYPES.SUCCESS);
    } catch (error) {
      return prepareErrorResponse(error);
    }
  };

  /**
   * Update entire branding config
   */
  updateBrandingConfig = async (
    baseAuthUrl: string,
    organizationId: string,
    payload: Partial<IBrandingConfig>
  ): Promise<any> => {
    try {
      const url = `${baseAuthUrl}/${BRANDING_CONFIG_ENDPOINT}/${organizationId}`;
      const apiResponse: any = await this.put(url, payload);
      return prepareResponseObject(apiResponse, RESPONSE_TYPES.SUCCESS);
    } catch (error) {
      return prepareErrorResponse(error);
    }
  };

  /**
   * Upload logo (light/dark/favicon) - multipart/form-data
   */
  uploadLogo = async (
    baseAuthUrl: string,
    organizationId: string,
    formData: FormData
  ): Promise<any> => {
    try {
      const url = `${baseAuthUrl}/${BRANDING_LOGO_ENDPOINT}/${organizationId}/logo`;
      const apiResponse: any = await this.post(url, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return prepareResponseObject(apiResponse, RESPONSE_TYPES.SUCCESS);
    } catch (error) {
      return prepareErrorResponse(error);
    }
  };

  /**
   * Delete a specific logo type
   */
  deleteLogo = async (
    baseAuthUrl: string,
    organizationId: string,
    logoType: string
  ): Promise<any> => {
    try {
      const url = `${baseAuthUrl}/${BRANDING_LOGO_ENDPOINT}/${organizationId}/logo/${logoType}`;
      const apiResponse: any = await this.delete(url);
      return prepareResponseObject(apiResponse, RESPONSE_TYPES.SUCCESS);
    } catch (error) {
      return prepareErrorResponse(error);
    }
  };

  /**
   * Update brand colors
   */
  updateColors = async (
    baseAuthUrl: string,
    organizationId: string,
    payload: IBrandingColors
  ): Promise<any> => {
    try {
      const url = `${baseAuthUrl}/${BRANDING_COLORS_ENDPOINT}/${organizationId}/colors`;
      const apiResponse: any = await this.patch(url, payload);
      return prepareResponseObject(apiResponse, RESPONSE_TYPES.SUCCESS);
    } catch (error) {
      return prepareErrorResponse(error);
    }
  };

  /**
   * Update typography settings
   */
  updateTypography = async (
    baseAuthUrl: string,
    organizationId: string,
    payload: IBrandingTypography
  ): Promise<any> => {
    try {
      const url = `${baseAuthUrl}/${BRANDING_TYPOGRAPHY_ENDPOINT}/${organizationId}/typography`;
      const apiResponse: any = await this.patch(url, payload);
      return prepareResponseObject(apiResponse, RESPONSE_TYPES.SUCCESS);
    } catch (error) {
      return prepareErrorResponse(error);
    }
  };

  /**
   * Update login page customization
   */
  updateLoginPage = async (
    baseAuthUrl: string,
    organizationId: string,
    payload: IBrandingLoginPage
  ): Promise<any> => {
    try {
      const url = `${baseAuthUrl}/${BRANDING_LOGIN_PAGE_ENDPOINT}/${organizationId}/login-page`;
      const apiResponse: any = await this.patch(url, payload);
      return prepareResponseObject(apiResponse, RESPONSE_TYPES.SUCCESS);
    } catch (error) {
      return prepareErrorResponse(error);
    }
  };

  /**
   * Upload login page background image
   */
  uploadLoginBackground = async (
    baseAuthUrl: string,
    organizationId: string,
    formData: FormData
  ): Promise<any> => {
    try {
      const url = `${baseAuthUrl}/${BRANDING_LOGIN_BACKGROUND_ENDPOINT}/${organizationId}/login-background`;
      const apiResponse: any = await this.post(url, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return prepareResponseObject(apiResponse, RESPONSE_TYPES.SUCCESS);
    } catch (error) {
      return prepareErrorResponse(error);
    }
  };

  /**
   * Update email branding
   */
  updateEmailBranding = async (
    baseAuthUrl: string,
    organizationId: string,
    payload: IBrandingEmailBranding
  ): Promise<any> => {
    try {
      const url = `${baseAuthUrl}/${BRANDING_EMAIL_ENDPOINT}/${organizationId}/email`;
      const apiResponse: any = await this.patch(url, payload);
      return prepareResponseObject(apiResponse, RESPONSE_TYPES.SUCCESS);
    } catch (error) {
      return prepareErrorResponse(error);
    }
  };

  /**
   * Update company metadata
   */
  updateMetadata = async (
    baseAuthUrl: string,
    organizationId: string,
    payload: IBrandingMetadata
  ): Promise<any> => {
    try {
      const url = `${baseAuthUrl}/${BRANDING_METADATA_ENDPOINT}/${organizationId}/metadata`;
      const apiResponse: any = await this.patch(url, payload);
      return prepareResponseObject(apiResponse, RESPONSE_TYPES.SUCCESS);
    } catch (error) {
      return prepareErrorResponse(error);
    }
  };

  /**
   * Add custom domain
   */
  addCustomDomain = async (
    baseAuthUrl: string,
    organizationId: string,
    payload: { domain: string }
  ): Promise<any> => {
    try {
      const url = `${baseAuthUrl}/${BRANDING_DOMAIN_ENDPOINT}/${organizationId}/domain`;
      const apiResponse: any = await this.post(url, payload);
      return prepareResponseObject(apiResponse, RESPONSE_TYPES.SUCCESS);
    } catch (error) {
      return prepareErrorResponse(error);
    }
  };

  /**
   * Verify custom domain DNS
   */
  verifyCustomDomain = async (
    baseAuthUrl: string,
    organizationId: string
  ): Promise<any> => {
    try {
      const url = `${baseAuthUrl}/${BRANDING_DOMAIN_VERIFY_ENDPOINT}/${organizationId}/domain/verify`;
      const apiResponse: any = await this.post(url, {});
      return prepareResponseObject(apiResponse, RESPONSE_TYPES.SUCCESS);
    } catch (error) {
      return prepareErrorResponse(error);
    }
  };

  /**
   * Delete custom domain
   */
  deleteCustomDomain = async (
    baseAuthUrl: string,
    organizationId: string
  ): Promise<any> => {
    try {
      const url = `${baseAuthUrl}/${BRANDING_DOMAIN_ENDPOINT}/${organizationId}/domain`;
      const apiResponse: any = await this.delete(url);
      return prepareResponseObject(apiResponse, RESPONSE_TYPES.SUCCESS);
    } catch (error) {
      return prepareErrorResponse(error);
    }
  };

  /**
   * Get custom domain status
   */
  getCustomDomainStatus = async (
    baseAuthUrl: string,
    organizationId: string
  ): Promise<any> => {
    try {
      const url = `${baseAuthUrl}/${BRANDING_DOMAIN_STATUS_ENDPOINT}/${organizationId}/domain/status`;
      const apiResponse: any = await this.get(url);
      return prepareResponseObject(apiResponse, RESPONSE_TYPES.SUCCESS);
    } catch (error) {
      return prepareErrorResponse(error);
    }
  };
}
